import type { PortalTheme } from '@/schema/gift-portal.schema'
import type { PortalTranslations } from '../../i18n'
import { getTranslations } from '../../i18n'

interface EmailPreviewProps {
  theme: PortalTheme
  translations?: PortalTranslations
  // When rendered inside the theme editor's preview pane the wrapper card has
  // a fixed virtual viewport (1280x800-ish, scaled). Without minHeight: 100%
  // the email content sits at its natural height and the wrapper's white
  // background bleeds through below the email body. In real email clients the
  // body is auto-height so we only apply this in preview mode.
  preview?: boolean
}

/**
 * Admin-side preview of the gift-portal confirmation email. This MUST mirror
 * templates/email/default/gift-portal-confirmation.hbs so the Email tab shows
 * the recipient exactly what they'll get: borderless item name + description,
 * then the code on the brand Primary-colour background — compact, left-aligned,
 * Kaartnummer / Pincode on separate rows — and a clickable "powered by
 * Alphabait" pill. Sample data stands in for the real values.
 */
export function EmailPreview({
  theme,
  translations,
  preview,
}: EmailPreviewProps) {
  const t = translations || getTranslations('nl')
  const bg = theme.colors.background || '#f5f5f5'
  const cardBg = theme.colors.cardBackground || '#ffffff'
  const cardFg = theme.colors.cardForeground || '#202124'
  const primary = theme.colors.primary || '#1a73e8'
  const font = theme.fontFamily || "'Space Grotesk', Arial, sans-serif"

  // Sample values — a realistic (long) card number so the admin sees how real
  // codes render in the compact layout, not an artificially short test code.
  const sampleName = 'Premium Gift Card'
  const sampleDescription = 'A versatile gift card for any occasion.'
  const sampleCardNumber = '309835476378'
  const samplePin = '4487'

  return (
    <div
      style={{
        backgroundColor: bg,
        fontFamily: font,
        margin: 0,
        padding: 0,
        width: '100%',
        ...(preview ? { minHeight: '100%' } : {}),
      }}
    >
      <table
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        style={{ backgroundColor: bg }}
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '24px 16px' }}>
              <table
                cellPadding={0}
                cellSpacing={0}
                style={{
                  maxWidth: 600,
                  width: '100%',
                  backgroundColor: cardBg,
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <tbody>
                  {theme.logoUrl && (
                    <tr>
                      <td
                        align="center"
                        style={{ padding: '32px 32px 16px 32px' }}
                      >
                        <img
                          src={theme.logoUrl}
                          alt="logo"
                          style={{
                            maxHeight: 80,
                            maxWidth: 200,
                            width: 'auto',
                            height: 'auto',
                            display: 'block',
                            objectFit: 'contain',
                          }}
                        />
                      </td>
                    </tr>
                  )}

                  {/* Intro */}
                  <tr>
                    <td
                      style={{
                        padding: '16px 32px 8px 32px',
                        color: cardFg,
                        fontFamily: font,
                        fontSize: 16,
                        fontWeight: 400,
                      }}
                    >
                      <p style={{ margin: 0 }}>{t.email.intro}</p>
                    </td>
                  </tr>

                  {/* Info card: name, description, code on separate
                      left-aligned rows — mirrors the gift-claimed email. */}
                  <tr>
                    <td style={{ padding: '8px 32px 24px 32px' }}>
                      <table
                        cellPadding={0}
                        cellSpacing={0}
                        style={{
                          width: '100%',
                          backgroundColor: cardBg,
                          borderRadius: 10,
                        }}
                      >
                        <tbody>
                          <tr>
                            <td
                              style={{
                                padding: 0,
                                textAlign: 'left',
                                fontFamily: font,
                                color: cardFg,
                              }}
                            >
                              <p
                                style={{
                                  margin: '0 0 7px 0',
                                  fontSize: 14,
                                  fontWeight: 700,
                                  lineHeight: '20px',
                                }}
                              >
                                {sampleName}
                              </p>
                              <p
                                style={{
                                  margin: '0 0 12px 0',
                                  fontSize: 14,
                                  fontWeight: 300,
                                  lineHeight: '20px',
                                }}
                              >
                                {sampleDescription}
                              </p>
                              {/* Code on the brand Primary background, compact
                                  and left-aligned, on separate rows. */}
                              <table
                                cellPadding={0}
                                cellSpacing={0}
                                style={{
                                  width: '100%',
                                  backgroundColor: primary,
                                  borderRadius: 8,
                                }}
                              >
                                <tbody>
                                  <tr>
                                    <td
                                      style={{
                                        padding: '12px 16px',
                                        fontSize: 14,
                                        lineHeight: '20px',
                                        color: '#ffffff',
                                      }}
                                    >
                                      <div>
                                        <span style={{ fontWeight: 600 }}>
                                          Kaartnummer:
                                        </span>{' '}
                                        <span
                                          style={{
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          {sampleCardNumber}
                                        </span>
                                      </div>
                                      <div style={{ marginTop: 6 }}>
                                        <span style={{ fontWeight: 600 }}>
                                          Pincode:
                                        </span>{' '}
                                        <span
                                          style={{
                                            fontWeight: 700,
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          {samplePin}
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>

                  {/* powered by Alphabait (clickable) */}
                  <tr>
                    <td
                      align="center"
                      style={{ padding: '16px 32px 24px 32px' }}
                    >
                      <table cellPadding={0} cellSpacing={0}>
                        <tbody>
                          <tr>
                            <td
                              style={{
                                backgroundColor: '#eceee7',
                                borderRadius: 64,
                                padding: '6px 12px',
                                fontFamily: font,
                                fontSize: 12,
                              }}
                            >
                              <a
                                href="https://www.alphabait.nl"
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  textDecoration: 'none',
                                  color: '#666',
                                }}
                              >
                                {t.email.poweredBy}{' '}
                                <strong style={{ color: '#333' }}>
                                  Alphabait
                                </strong>
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
